const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

/**
 * Digits, one optional decimal point with up to `maxDecimals` decimals, optional leading minus.
 * `maxDecimals: 0` drops the point entirely, so integer-only fields can't hold a dangling "12.".
 */
export function sanitizeCurrencyInput(value: string, maxDecimals = 2): string {
  const isNegative = value.trimStart().startsWith("-");
  const [whole = "", ...rest] = value.replace(/[^\d.]/g, "").split(".");
  const decimals = rest.join("").slice(0, maxDecimals);
  const keepPoint = maxDecimals > 0 && rest.length > 0;

  return `${isNegative ? "-" : ""}${keepPoint ? `${whole}.${decimals}` : whole}`;
}

/** Groups the integer part in thousands: "1000.5" → "1,000.5". */
export function formatNumberWithCommas(value: string): string {
  const parts = value.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return parts.join(".");
}

/** Scale suffix for a currency amount: "bn" from a billion, "mm" from a million, else none. */
export function getCurrencySuffix(value: string): string {
  const numeric = parseFloat(value.replace(/,/g, ""));

  if (isNaN(numeric)) return "";
  if (Math.abs(numeric) >= BILLION) return "bn";
  if (Math.abs(numeric) >= MILLION) return "mm";

  return "";
}

/**
 * Read-only currency display: amounts from a million up are scaled to match the suffix from
 * `getCurrencySuffix` (213232232 → "213.23" + "mm"), smaller ones just get thousands separators.
 */
export function getCurrencyDisplayValue(value: string): string {
  const raw = value.replace(/,/g, "");
  const numeric = parseFloat(raw);

  if (isNaN(numeric)) return value;

  const scale = Math.abs(numeric) >= BILLION ? BILLION : MILLION;

  if (Math.abs(numeric) >= MILLION) {
    return (numeric / scale).toFixed(2).replace(/\.?0+$/, "");
  }

  return formatNumberWithCommas(raw);
}
